import qs from 'querystring';
import Http from 'services/Http';
import { encryptFile } from 'utils/e2eEncryption';
import captureVideoPoster from 'utils/captureVideoPoster';
import computeContentMd5Base64 from 'utils/computeContentMd5Base64';
import parseFileName from 'utils/getFileExtension';
import readImageSize from 'utils/readImageSize';

export const MEDIA_LIMIT = 25;

class Attachments {
  async request(id, type, page = 1, perPage = MEDIA_LIMIT, sortBy = 'createdAt', sort = 'desc') {
    const params = {
      page,
      perPage,
      sortBy,
      sort,
      type,
    };

    const { data } = await Http.get(`/v1/message/${id}/attachments?${qs.stringify(params)}`);

    return data;
  }

  async links(_id, page, perPage = MEDIA_LIMIT) {
    const { links, totalCount } = await this.request(_id, 'link', page, perPage);

    return {
      links: links.filter(l => !!l),
      totalCount,
    };
  }

  media(_id, page, perPage = MEDIA_LIMIT) {
    return this.request(_id, 'media', page, perPage);
  }

  docs(_id, page, perPage = MEDIA_LIMIT, field, sort, type = 'doc') {
    return this.request(_id, type, page, perPage, field, sort);
  }

  forms(_id, page, perPage = MEDIA_LIMIT) {
    return this.request(_id, 'form', page, perPage);
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.sanitizePdf] - When true, API rewrites PDF with qpdf (xref/structure) before storing.
   * @param {File} [options.thumbnail] - Client JPEG poster. Videos capture one automatically
   *   (same sign-url `thumbnail` path as RN) unless this is passed or set to null.
   */
  async upload(
    company,
    groupId,
    file,
    locale,
    onUploadProgress,
    getCancelFunc,
    fileName,
    options = {},
  ) {
    const { sanitizePdf = false, meta, thumbnail: thumbnailOption } = options || {};
    const { type: mimeType } = file;
    const name = fileName || file.name;
    const { ext } = parseFileName(name);
    const contentMd5 = await computeContentMd5Base64(file);
    let thumbnail = thumbnailOption;
    let videoImageSize;
    if (thumbnail === undefined && mimeType?.startsWith('video/')) {
      const captured = await captureVideoPoster(file);
      thumbnail = captured?.poster ?? null;
      videoImageSize = captured?.imageSize;
    }
    const imageSize = mimeType?.startsWith('image/') ? await readImageSize(file) : null;
    const thumbnailContentMd5 = thumbnail ? await computeContentMd5Base64(thumbnail) : undefined;
    const {
      data: { id, url, thumbnailUrl },
    } = await Http.post('/v1/attachment/sign-url', {
      mimeType,
      ext,
      groupId,
      contentMd5,
      thumbnail: !!thumbnail,
      ...(thumbnailContentMd5 ? { thumbnailContentMd5 } : {}),
    });
    if (thumbnailUrl && thumbnail) {
      await Http.putS3(thumbnailUrl, thumbnail, {
        headers: {
          'Content-Type': 'image/jpeg',
          'Content-MD5': thumbnailContentMd5,
        },
        timeout: 0,
      });
    }
    await Http.putS3(url, file, {
      headers: {
        'Content-Type': mimeType,
        'Content-MD5': contentMd5,
        // Note: x-amz-tagging is already in the presigned URL query string
        // Cache-Control is not required and not signed
        // Content-Length is set automatically by the browser
      },
      timeout: 0,
      onUploadProgress,
      getCancelFunc,
    });
    const uploadedPayload = { id, name, groupId };
    if (sanitizePdf) {
      uploadedPayload.sanitizePdf = true;
    }
    const uploadedMeta = { ...meta };
    if (videoImageSize) {
      uploadedMeta.imageSize = videoImageSize;
    } else if (imageSize) {
      uploadedMeta.imageSize = imageSize;
    }
    if (Object.keys(uploadedMeta).length) {
      uploadedPayload.meta = uploadedMeta;
    }
    const response = await Http.patch('/v1/attachment/sign-url/uploaded', uploadedPayload);
    return response.data;
  }

  async uploadWb(company, file, onUploadProgress, getCancelFunc, fileName, slug, aesKey) {
    const { type: mimeType } = file;
    const name = fileName || file.name;
    const { ext } = parseFileName(name);
    const groupId = 'whistleblower';
    const postFile = await encryptFile(file, aesKey);
    const contentMd5 = await computeContentMd5Base64(postFile);
    const {
      data: { id, url },
    } = await Http.post(
      slug ? `/v1/wb/portal/${slug}/attachment/sign-url` : '/v1/wb/attachment/sign-url',
      {
        mimeType,
        ext,
        groupId,
        contentMd5,
      },
    );
    await Http.putS3(url, postFile, {
      headers: {
        'Content-Type': mimeType,
        'Content-MD5': contentMd5,
        // Note: x-amz-tagging is already in the presigned URL query string
        // Cache-Control is not required and not signed
        // Content-Length is set automatically by the browser
      },
      timeout: 0,
      onUploadProgress,
      getCancelFunc,
    });
    const response = await Http.patch(
      slug
        ? `/v1/wb/portal/${slug}/attachment/sign-url/uploaded`
        : '/v1/wb/attachment/sign-url/uploaded',
      {
        id,
        name,
        groupId,
        encrypted: true,
      },
    );
    return response.data;
  }
}

export default new Attachments();
